<template>
  <div class="issued-credit-certificate-page">
    <ManagementListPage title="Certidões de crédito"
      subtitle="Emita, consulte downloads e verifique a autenticidade das certidões."
      :result-text="certificateResultText" :breadcrumbs="breadcrumbs" page-class="issued-certificate-list">
      <template #actions>
        <v-btn v-if="tab === 0" color="primary" elevation="0" @click="issueDialog = true">
          <v-icon left small>mdi-file-certificate-outline</v-icon>Emitir certidão
        </v-btn>
      </template>
      <template #navigation>
          <v-tabs v-model="tab" class="management-list-tabs" background-color="transparent">
            <v-tab>Certidões</v-tab>
            <v-tab>Downloads</v-tab>
            <v-tab>Verificar autenticidade</v-tab>
          </v-tabs>
      </template>
      <template #filters>
        <div v-if="tab === 0">
          <div class="op-filter-toolbar">
            <v-text-field v-model="certificateFilter.registration" dense outlined hide-details clearable
              prepend-inner-icon="mdi-magnify" label="Buscar por CPF/CNPJ"
              @keyup.enter="loadCertificates(true)" />
            <v-btn outlined color="primary" @click="showCertificateFilters = !showCertificateFilters">
              <v-icon left small>mdi-tune-variant</v-icon>Filtros
              <span v-if="certificateAdvancedFilterCount" class="op-filter-count">{{ certificateAdvancedFilterCount }}</span>
              <v-icon right small>{{ showCertificateFilters ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </v-btn>
            <v-btn color="primary" elevation="0" @click="loadCertificates(true)">
              <v-icon left small>mdi-magnify</v-icon>Buscar
            </v-btn>
          </div>
          <v-expand-transition>
            <div v-if="showCertificateFilters" class="op-filter-panel mt-3 pt-3">
              <div class="op-filter-panel-head"><div><strong>Filtros avançados</strong><span>Refine as certidões exibidas na listagem.</span></div></div>
              <v-row dense class="op-filter-grid">
                <v-col cols="12" md="4"><div class="op-field"><label>Código de autenticação</label><v-text-field dense outlined hide-details placeholder="Informe o código" v-model="certificateFilter.auth_code" clearable /></div></v-col>
                <v-col cols="12" md="4"><div class="op-field"><label>Classe</label><v-select dense outlined hide-details placeholder="Todas as classes" :items="creditorTypes" item-value="id" item-text="name" v-model="certificateFilter.creditor_type" clearable /></div></v-col>
                <v-col cols="12" md="4"><div class="op-field"><label>Moeda</label><v-select dense outlined hide-details placeholder="Todas as moedas" :items="currencyTypes" item-value="id" item-text="name" v-model="certificateFilter.currency" clearable /></div></v-col>
              </v-row>
            </div>
          </v-expand-transition>
          <div v-if="showCertificateFilters" class="op-filter-footer">
            <span>{{ certificateAdvancedFilterCount ? `${certificateAdvancedFilterCount} filtro(s) ativo(s)` : 'Nenhum filtro adicional aplicado' }}</span>
            <v-btn v-if="certificateAdvancedFilterCount" text small color="primary" @click="clearCertificateFilter">Limpar filtros</v-btn>
          </div>
        </div>
        <div v-else-if="tab === 1">
          <div class="op-filter-toolbar">
            <v-text-field v-model="downloadFilter.registration" dense outlined hide-details clearable
              prepend-inner-icon="mdi-magnify" label="Buscar por CPF/CNPJ"
              @keyup.enter="loadDownloads(true)" />
            <v-btn outlined color="primary" @click="showDownloadFilters = !showDownloadFilters">
              <v-icon left small>mdi-tune-variant</v-icon>Filtros
              <span v-if="downloadAdvancedFilterCount" class="op-filter-count">{{ downloadAdvancedFilterCount }}</span>
              <v-icon right small>{{ showDownloadFilters ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </v-btn>
            <v-btn color="primary" elevation="0" @click="loadDownloads(true)">
              <v-icon left small>mdi-magnify</v-icon>Buscar
            </v-btn>
          </div>
          <v-expand-transition>
            <div v-if="showDownloadFilters" class="op-filter-panel mt-3 pt-3">
              <div class="op-filter-panel-head"><div><strong>Filtros avançados</strong><span>Refine os downloads exibidos na listagem.</span></div></div>
              <v-row dense class="op-filter-grid">
                <v-col cols="12" md="6"><div class="op-field"><label>Origem</label><v-select dense outlined hide-details placeholder="Todas as origens" :items="downloadSources" v-model="downloadFilter.source" clearable /></div></v-col>
                <v-col cols="12" md="6"><div class="op-field"><label>Quem baixou</label><v-select dense outlined hide-details placeholder="Todos os perfis" :items="actorTypes" item-value="id" item-text="name" v-model="downloadFilter.downloaded_by_type" clearable /></div></v-col>
              </v-row>
            </div>
          </v-expand-transition>
          <div v-if="showDownloadFilters" class="op-filter-footer">
            <span>{{ downloadAdvancedFilterCount ? `${downloadAdvancedFilterCount} filtro(s) ativo(s)` : 'Nenhum filtro adicional aplicado' }}</span>
            <v-btn v-if="downloadAdvancedFilterCount" text small color="primary" @click="clearDownloadFilter">Limpar filtros</v-btn>
          </div>
        </div>
        <div v-else class="certificate-verify-intro">
          <v-icon small color="primary">mdi-shield-check-outline</v-icon>
          Valide uma certidão pelo código de autenticação ou pelo arquivo PDF.
        </div>
      </template>
      <template #table>
          <v-tabs-items v-model="tab">
            <v-tab-item>
              <v-card-text class="pa-0">
                <v-data-table class="app-table management-list-table management-minimal-table" :headers="certificateHeaders" :items="certificates"
                  :server-items-length="certificateTotal" :options.sync="certificateOptions" :loading="loadingCertificates"
                  :disable-sort="true" :footer-props="footerOptions" no-data-text="Nenhuma certidão encontrada">
                  <template v-slot:[`item.creditor_registration`]="{ item }">
                    {{ formatCpfCnpj(item.creditor_registration) }}
                  </template>
                  <template v-slot:[`item.creditor_type`]="{ item }">
                    {{ item.creditor_type | enum(creditorTypes, "id", "name") }}
                  </template>
                  <template v-slot:[`item.currency`]="{ item }">
                    {{ item.currency | enum(currencyTypes, "id", "name") }}
                  </template>
                  <template v-slot:[`item.amount`]="{ item }">
                    {{ formatCurrency(item.amount, item.currency) }}
                  </template>
                  <template v-slot:[`item.issued_at`]="{ item }">
                    {{ item.issued_at | defaultDate("DD/MM/YYYY HH:mm") }}
                  </template>
                  <template v-slot:[`item.valid_until`]="{ item }">
                    {{ item.valid_until | defaultDate("DD/MM/YYYY HH:mm") }}
                  </template>
                  <template v-slot:[`item.state`]="{ item }">
                    <v-chip small class="op-table-badge" :color="certificateState(item).color" dark>{{ certificateState(item).text }}</v-chip>
                  </template>
                  <template v-slot:[`item.actions`]="{ item }">
                    <div class="op-row-actions">
                    <v-tooltip bottom>
                      <template #activator="{ on }">
                        <v-btn v-on="on" icon small @click="downloadCertificate(item)"><v-icon small>mdi-download-outline</v-icon></v-btn>
                      </template>
                      <span>Baixar PDF</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template #activator="{ on }">
                        <v-btn v-on="on" icon small @click="reissue(item)"><v-icon small>mdi-file-refresh-outline</v-icon></v-btn>
                      </template>
                      <span>Reemitir</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template #activator="{ on }">
                        <v-btn v-on="on" icon small color="error" @click="openInvalidate(item)"><v-icon small>mdi-cancel</v-icon></v-btn>
                      </template>
                      <span>Invalidar</span>
                    </v-tooltip></div>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-tab-item>

            <v-tab-item>
              <v-card-text class="pa-0">
                <v-data-table class="app-table management-list-table management-minimal-table" :headers="downloadHeaders" :items="downloads"
                  :server-items-length="downloadTotal" :options.sync="downloadOptions" :loading="loadingDownloads"
                  :disable-sort="true" :footer-props="footerOptions" no-data-text="Nenhum download encontrado">
                  <template v-slot:[`item.creditor_registration`]="{ item }">
                    {{ formatCpfCnpj(item.creditor_registration) }}
                  </template>
                  <template v-slot:[`item.downloaded_by_type`]="{ item }">
                    {{ item.downloaded_by_type | enum(actorTypes, "id", "name") }}
                  </template>
                  <template v-slot:[`item.created_at`]="{ item }">
                    {{ item.created_at | defaultDate("DD/MM/YYYY HH:mm:ss") }}
                  </template>
                </v-data-table>
              </v-card-text>
            </v-tab-item>

            <v-tab-item>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="5">
                    <v-card outlined>
                      <v-card-title class="text-subtitle-1 font-weight-bold">Verificar por código</v-card-title>
                      <v-card-text>
                        <v-text-field dense outlined label="Código de autenticação" v-model="verify.auth_code" hide-details="auto" />
                      </v-card-text>
                      <v-card-actions>
                        <v-spacer />
                        <v-btn color="primary" elevation="0" @click="verifyByCode">Verificar</v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="7">
                    <v-card outlined>
                      <v-card-title class="text-subtitle-1 font-weight-bold">Verificar por PDF</v-card-title>
                      <v-card-text>
                        <v-file-input dense outlined accept="application/pdf" label="PDF da certidão" v-model="verify.file" hide-details="auto" />
                      </v-card-text>
                      <v-card-actions>
                        <v-spacer />
                        <v-btn color="primary" elevation="0" @click="verifyByUpload">Verificar</v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-col>
                </v-row>

                <v-alert v-if="verify.result" class="mt-4" :type="verify.result.valid ? 'success' : 'error'" outlined>
                  <strong>{{ verify.result.status || 'Resultado' }}:</strong> {{ verify.result.reason }}
                </v-alert>
              </v-card-text>
            </v-tab-item>
          </v-tabs-items>
      </template>
    </ManagementListPage>

    <v-dialog v-model="issueDialog" max-width="560px">
      <v-card>
        <v-card-title>Emitir certidão</v-card-title>
        <v-card-text>
          <v-text-field dense outlined label="CPF/CNPJ" v-model="issue.registration" hide-details="auto" class="mb-3" />
          <v-select dense outlined label="Classe" :items="creditorTypes" item-value="id" item-text="name" v-model="issue.creditor_type" hide-details="auto" class="mb-3" />
          <v-select dense outlined label="Moeda" :items="currencyTypes" item-value="id" item-text="name" v-model="issue.currency" hide-details="auto" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="issueDialog = false">Cancelar</v-btn>
          <v-btn color="primary" elevation="0" @click="issueAndDownload">Emitir e baixar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="invalidateDialog" max-width="560px">
      <v-card>
        <v-card-title>Invalidar certidão</v-card-title>
        <v-card-text>
          <v-textarea outlined label="Motivo" v-model="invalidate.reason" rows="3" hide-details="auto" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="invalidateDialog = false">Cancelar</v-btn>
          <v-btn color="error" elevation="0" @click="invalidateCertificate">Invalidar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import creditorTypes from "@/common/data/creditor-types.data";
import currencyTypes from "@/common/data/currency-types.data";

export default {
  name: "IssuedCreditCertificateList",
  computed: {
    certificateResultText() {
      if (this.tab === 0) return `${this.certificateTotal} certidões encontradas`;
      if (this.tab === 1) return `${this.downloadTotal} downloads encontrados`;
      return "Validação por código ou arquivo PDF";
    },
    certificateAdvancedFilterCount() {
      return ["auth_code", "creditor_type", "currency"]
        .filter((key) => this.certificateFilter[key] !== "" && this.certificateFilter[key] !== null && this.certificateFilter[key] !== undefined).length;
    },
    downloadAdvancedFilterCount() {
      return ["source", "downloaded_by_type"]
        .filter((key) => this.downloadFilter[key] !== "" && this.downloadFilter[key] !== null && this.downloadFilter[key] !== undefined).length;
    },
  },
  data: () => ({
    tab: 0,
    showCertificateFilters: false,
    showDownloadFilters: false,
    breadcrumbs: [
      { text: "Homepage", disabled: false, to: "/home", exact: true },
      { text: "Certidão de crédito", disabled: true, exact: true },
    ],
    creditorTypes,
    currencyTypes,
    downloadSources: ["creditor_summary", "proceeding", "backoffice"],
    actorTypes: [
      { id: 1, name: "Credor" },
      { id: 2, name: "Procurador" },
      { id: 3, name: "Administrador" },
    ],
    certificateFilter: {},
    downloadFilter: {},
    issue: {},
    verify: { auth_code: "", file: null, result: null },
    invalidate: { item: null, reason: "" },
    issueDialog: false,
    invalidateDialog: false,
    certificates: [],
    certificateTotal: 0,
    loadingCertificates: false,
    certificateOptions: { page: 1, itemsPerPage: 10 },
    downloads: [],
    downloadTotal: 0,
    loadingDownloads: false,
    downloadOptions: { page: 1, itemsPerPage: 10 },
    footerOptions: {
      itemsPerPageText: "Itens por página:",
      itemsPerPageOptions: [10, 20, 30, 40, 50],
      pageText: "{0}-{1} de {2}",
    },
    certificateHeaders: [
      { text: "Código", value: "auth_code" },
      { text: "Credor", value: "creditor_name" },
      { text: "CPF/CNPJ", value: "creditor_registration" },
      { text: "Classe", value: "creditor_type" },
      { text: "Moeda", value: "currency" },
      { text: "Valor", value: "amount" },
      { text: "Emitida em", value: "issued_at" },
      { text: "Válida até", value: "valid_until" },
      { text: "Situação", value: "state" },
      { text: "Ações", value: "actions", sortable: false },
    ],
    downloadHeaders: [
      { text: "Credor", value: "creditor_name" },
      { text: "CPF/CNPJ", value: "creditor_registration" },
      { text: "Quem baixou", value: "downloaded_by_type" },
      { text: "Nome", value: "downloaded_by_name" },
      { text: "Origem", value: "source" },
      { text: "IP", value: "ip_address" },
      { text: "Data", value: "created_at" },
    ],
  }),
  watch: {
    certificateOptions: { handler() { this.loadCertificates(); }, deep: true },
    downloadOptions: { handler() { this.loadDownloads(); }, deep: true },
  },
  created() {
    this.loadCertificates();
    this.loadDownloads();
  },
  methods: {
    async loadCertificates(resetPage = false) {
      if (resetPage) this.certificateOptions.page = 1;
      this.loadingCertificates = true;
      try {
        const result = await axios.get("/api/admin/issued-credit-certificates", {
          params: { ...this.clean(this.certificateFilter), page: this.certificateOptions.page, perPage: this.certificateOptions.itemsPerPage },
        });
        this.certificates = result.data.content.data || [];
        this.certificateTotal = result.data.content.total || 0;
      } catch (error) {
        this.notifyError(error);
      } finally {
        this.loadingCertificates = false;
      }
    },
    async loadDownloads(resetPage = false) {
      if (resetPage) this.downloadOptions.page = 1;
      this.loadingDownloads = true;
      try {
        const result = await axios.get("/api/admin/issued-credit-certificates/downloads", {
          params: { ...this.clean(this.downloadFilter), page: this.downloadOptions.page, perPage: this.downloadOptions.itemsPerPage },
        });
        this.downloads = result.data.content.data || [];
        this.downloadTotal = result.data.content.total || 0;
      } catch (error) {
        this.notifyError(error);
      } finally {
        this.loadingDownloads = false;
      }
    },
    async downloadCertificate(item) {
      await this.downloadBlob(`/api/admin/issued-credit-certificates/${item.id}/download`, {}, `certidao_credito_${item.auth_code}.pdf`);
      this.loadDownloads();
    },
    async issueAndDownload() {
      await this.downloadBlob("/api/admin/issued-credit-certificates/issue", this.issue, "certidao_credito.pdf", "post");
      this.issueDialog = false;
      this.loadCertificates();
      this.loadDownloads();
    },
    async reissue(item) {
      try {
        await axios.post(`/api/admin/issued-credit-certificates/${item.id}/reissue`);
        this.$dialog.notify.success("Certidão reemitida.", { position: "top-right", timeout: 2000 });
        this.loadCertificates();
      } catch (error) {
        this.notifyError(error);
      }
    },
    openInvalidate(item) {
      this.invalidate = { item, reason: "" };
      this.invalidateDialog = true;
    },
    async invalidateCertificate() {
      try {
        await axios.patch(`/api/admin/issued-credit-certificates/${this.invalidate.item.id}/invalidate`, { reason: this.invalidate.reason });
        this.invalidateDialog = false;
        this.loadCertificates();
      } catch (error) {
        this.notifyError(error);
      }
    },
    async verifyByCode() {
      try {
        const result = await axios.post("/api/admin/issued-credit-certificates/verify/code", { auth_code: this.verify.auth_code });
        this.verify.result = result.data.content;
      } catch (error) {
        this.notifyError(error);
      }
    },
    async verifyByUpload() {
      try {
        const form = new FormData();
        form.append("file", this.verify.file);
        const result = await axios.post("/api/admin/issued-credit-certificates/verify/upload", form);
        this.verify.result = result.data.content;
      } catch (error) {
        this.notifyError(error);
      }
    },
    async downloadBlob(url, payload, fallbackName, method = "get") {
      try {
        const result = await axios({ url, method, params: method === "get" ? payload : {}, data: method === "post" ? payload : {}, responseType: "blob" });
        const blobUrl = window.URL.createObjectURL(new Blob([result.data], { type: "application/pdf" }));
        const link = document.createElement("a");
        link.href = blobUrl;
        link.setAttribute("download", this.filenameFromHeaders(result.headers) || fallbackName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      } catch (error) {
        this.notifyError(error);
      }
    },
    filenameFromHeaders(headers) {
      const disposition = headers && headers["content-disposition"];
      const match = disposition && disposition.match(/filename="?([^"]+)"?/);
      return match ? match[1] : null;
    },
    certificateState(item) {
      if (item.invalidated_at) return { text: "Invalidada", color: "error" };
      if (item.valid_until && new Date(item.valid_until) < new Date()) return { text: "Vencida", color: "warning" };
      return { text: "Válida", color: "success" };
    },
    clearCertificateFilter() {
      this.certificateFilter = {};
      this.loadCertificates(true);
    },
    clearDownloadFilter() {
      this.downloadFilter = {};
      this.loadDownloads(true);
    },
    clean(value) {
      return Object.fromEntries(Object.entries(value || {}).filter(([, item]) => item !== "" && item !== null && item !== undefined));
    },
    formatCpfCnpj(value) {
      const digits = String(value || "").replace(/\D/g, "");
      if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
      return value || "-";
    },
    formatCurrency(value, currency) {
      const code = { 1: "BRL", 2: "USD", 3: "EUR" }[Number(currency)] || "BRL";
      return Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: code });
    },
    notifyError(error) {
      this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 3000 });
    },
  },
};
</script>

<style scoped>
.issued-credit-certificate-page ::v-deep .op-filter-toolbar > .v-input { flex:0 1 520px; }
.issued-credit-certificate-page ::v-deep .op-row-actions {
  display:flex;
  align-items:center;
  justify-content:flex-end;
  gap:2px;
}
.issued-credit-certificate-page ::v-deep .op-row-actions .v-btn { width:28px; height:28px; }
.certificate-verify-intro {
  display:flex;
  align-items:center;
  gap:8px;
  min-height:36px;
  color:#64748b;
  font-size:11px;
}
</style>
